import { Center, Fade, Flex, Spinner, Text, useToast } from '@chakra-ui/react';
import { useState, useMemo, useCallback } from 'react';
import { useDebounce } from 'use-debounce';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useParams } from 'react-router-dom';

import { states, availableStates, getStateKeyByValue } from '@duke-hq/svc-api/lib/states';
import { StatesFilter } from '@duke-hq/filters/components/StatesFilter';
import { EnabledFilterSiteLocation } from '@duke-hq/filters/components/EnabledFilterSiteLocation';
import { SearchSiteLocation } from '@duke-hq/filters/components/SearchSiteLocation';
import { SiteList, type SiteLocationType } from './RegionSites/SiteList';
import { StatesSchema } from '@duke-hq/svc-api-db/schemas/enums/States.schema';
import { LocationError } from './LocationError';

import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { RegionsLocationsURLParams } from './types';

export type SiteRegionType = NonNullable<RouterOutputs['providers']['locations']['getSiteRegion']>;

export function RegionSites({ region }: { region?: SiteRegionType }) {
  const { providerId, regionId } = useParams<RegionsLocationsURLParams>();
  const [search, setSearch] = useState<string>('');
  const [showFilter, setShowFilter] = useState<string | null>(null);

  const initializeStateFilter = () => {
    if (region) {
      const stateKey = getStateKeyByValue(region?.name);
      return stateKey ? stateKey : null;
    }
  };

  const [stateFilter, setStateFilter] = useState<string | null>(initializeStateFilter() ?? null);

  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const [debouncedSearch] = useDebounce(search, 500);

  const createSiteRegionSiteLocations =
    trpcAdmin.providers.locations.createSiteRegionSiteLocations.useMutation({
      onSuccess: async () => {
        toast({
          title: t`Site added successfully`,
          status: 'success',
          isClosable: true,
          duration: 3000,
        });

        await utils.providers.locations.listSiteRegionsWithPagination.refetch();
      },
    });

  const deleteSiteRegionSiteLocations =
    trpcAdmin.providers.locations.deleteSiteRegionSiteLocations.useMutation({
      onSuccess: async () => {
        toast({
          title: t`Site removed successfully`,
          status: 'success',
          isClosable: true,
          duration: 3000,
        });

        await utils.providers.locations.listSiteRegionsWithPagination.refetch();
      },
    });

  const toggleSiteAvailability = useCallback(
    (site: SiteLocationType) => {
      const isPrimaryRegion = region?.is_default ?? false;
      const isSiteInRegion = site.state ? states[site.state] === region?.name : false;
      const siteAvailability = isPrimaryRegion && !isSiteInRegion;

      if (siteAvailability) {
        toast({
          title: t`Sites located outside the primary state (${region?.name ?? ''}) cannot be linked.`,
          status: 'error',
          isClosable: true,
          duration: 3000,
        });
        return;
      }

      const providerIdNum = Number(providerId);
      if (isNaN(providerIdNum)) {
        toast({
          title: t`Provider ID is not valid.`,
          status: 'error',
          isClosable: true,
          duration: 3000,
        });
        return;
      }

      if (!site.assigned) {
        createSiteRegionSiteLocations.mutate({
          providerId: providerIdNum,
          regionId: regionId,
          siteId: site.id,
        });
      } else {
        if (typeof regionId === 'string') {
          deleteSiteRegionSiteLocations.mutate({
            providerId: providerIdNum,
            regionId: regionId,
            siteId: site.id,
          });
        } else {
          toast({
            title: t`Region ID is not valid.`,
            status: 'error',
            isClosable: true,
            duration: 3000,
          });
        }
      }
    },
    [
      region,
      regionId,
      createSiteRegionSiteLocations,
      deleteSiteRegionSiteLocations,
      toast,
      providerId,
    ],
  );

  const { data, isPending } =
    trpcAdmin.providers.locations.listSiteLocationsBySiteRegionId.useQuery(
      {
        search: debouncedSearch,
        filters: {
          state: StatesSchema.safeParse(stateFilter).data,
          available:
            showFilter === 'Show enabled'
              ? true
              : showFilter === 'Show disabled'
                ? false
                : undefined,
        },
        providerId: Number(providerId),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        regionId: regionId!,
      },
      { enabled: Boolean(!!providerId && !!regionId) },
    );

  const sites = useMemo(() => {
    if (typeof data === 'undefined') {
      return [];
    }

    return data.map((site) => {
      return {
        assigned: site.assigned,
        id: site.id,
        name: site.name,
        // TODO: move this parse to the API
        state: StatesSchema.safeParse(site.state).data,
        country: null,
        description: null,
        type: null,
        count: null,
      } satisfies SiteLocationType;
    });
  }, [data]);

  if (!providerId || !regionId) {
    return <LocationError message={t`An error occurred, providerId or regionId are required.`} />;
  }

  return (
    <>
      <Flex
        w="full"
        gap={2}
        p={4}
        style={{ position: 'sticky', top: '0', zIndex: '1' }}
        bg="bg.canvas"
      >
        <EnabledFilterSiteLocation
          showFilter={showFilter}
          setShowFilter={setShowFilter}
          options={[
            { value: 'Show enabled', label: t`Show enabled` },
            { value: 'Show disabled', label: t`Show disabled` },
          ]}
          defaultLabel={t`Show all`}
        />
        <StatesFilter
          stateFilter={stateFilter}
          stateOptions={availableStates}
          setStateFilter={setStateFilter}
        />
        <SearchSiteLocation search={search} setSearch={setSearch} placeholder={t`Search`} />
      </Flex>

      {!isPending && sites?.length === 0 && (
        <Center p={8}>
          <Text textAlign="center">
            <Trans>No sites found</Trans>
          </Text>
        </Center>
      )}

      {Boolean(isPending && !sites) && (
        <Fade
          in={true}
          transition={{
            enter: {
              delay: 0.3,
              duration: 0,
            },
          }}
        >
          <Center p={8}>
            <Spinner size="xl" transitionDelay={'2900ms'} />
          </Center>
        </Fade>
      )}

      {sites != null ? (
        <SiteList sites={sites} region={region} toggleSiteAvailability={toggleSiteAvailability} />
      ) : null}
    </>
  );
}
